// 快照讀取：逾時、重試（指數退避）、schema 版本檢查。分片檔名帶 hash，同一個檔案在一次
// 瀏覽中只下載一次（promise 快取）；失敗的請求不留在快取裡，重試時會重新下載。
import { type GameDetail, type IndexRow, type Manifest, SNAPSHOT_SCHEMA } from '@dayone/core/snapshot';

export type LoadErrorKind = 'network' | 'notFound' | 'schema' | 'unknown';

export class LoadError extends Error {
  readonly kind: LoadErrorKind;
  constructor(kind: LoadErrorKind, message: string) {
    super(message);
    this.name = 'LoadError';
    this.kind = kind;
  }
}

export interface FetchOptions {
  retries?: number;
  timeoutMs?: number;
  fetchImpl?: typeof fetch;
  sleep?: (ms: number) => Promise<void>;
}

export async function fetchJson<T>(url: string, opts: FetchOptions = {}): Promise<T> {
  const {
    retries = 2,
    timeoutMs = 15_000,
    fetchImpl = (...a: Parameters<typeof fetch>) => fetch(...a),
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  } = opts;
  let last: LoadError = new LoadError('unknown', url);
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) await sleep(500 * 2 ** (attempt - 1));
    try {
      const res = await fetchImpl(url, { signal: AbortSignal.timeout(timeoutMs) });
      if (res.status === 404) throw new LoadError('notFound', `${url} 404`);
      if (!res.ok) {
        last = new LoadError('network', `${url} HTTP ${res.status}`);
        if (res.status >= 500 || res.status === 429) continue;
        throw last;
      }
      return (await res.json()) as T;
    } catch (e) {
      if (e instanceof LoadError && e.kind !== 'network') throw e;
      last = e instanceof LoadError ? e : new LoadError('network', e instanceof Error ? e.message : String(e));
    }
  }
  throw last;
}

export interface SnapshotClient {
  manifest(): Promise<Manifest>;
  shard(file: string): Promise<IndexRow[]>;
  /** 詳情檔：檔名帶 hash，同一版本只下載一次 */
  detail(id: string, hash: string): Promise<GameDetail>;
}

export function createSnapshotClient(base = '/data', opts: FetchOptions = {}): SnapshotClient {
  const cache = new Map<string, Promise<IndexRow[]>>();
  const details = new Map<string, Promise<GameDetail>>();
  /** 快取 promise；失敗的不留在快取，重試時重新下載 */
  function cached<T>(map: Map<string, Promise<T>>, key: string, load: () => Promise<T>): Promise<T> {
    let p = map.get(key);
    if (!p) {
      p = load();
      p.catch(() => map.delete(key));
      map.set(key, p);
    }
    return p;
  }
  return {
    async manifest() {
      const m = await fetchJson<Manifest>(`${base}/manifest.json`, opts);
      if (typeof m?.schema !== 'number' || !Array.isArray(m.shards)) {
        throw new LoadError('unknown', 'manifest 格式不符');
      }
      // 快照格式比前端新：需要重新整理載入新版前端
      if (m.schema > SNAPSHOT_SCHEMA) throw new LoadError('schema', `schema ${m.schema} > ${SNAPSHOT_SCHEMA}`);
      return m;
    },
    shard(file) {
      return cached(cache, file, async () => {
        const rows = await fetchJson<IndexRow[]>(`${base}/${file}`, opts);
        if (!Array.isArray(rows)) throw new LoadError('unknown', `${file} 格式不符`);
        return rows;
      });
    },
    detail(id, hash) {
      return cached(details, `${id}.${hash}`, async () => {
        const d = await fetchJson<GameDetail>(`${base}/g/${id}.${hash}.json`, opts);
        if (d?.id !== id) throw new LoadError('unknown', `${id} 詳情格式不符`);
        return d;
      });
    },
  };
}
